import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './ReportStudentList.css';
import Swal from 'sweetalert2';
import { reportStudentListAPI } from '../../services/api';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';

// Register ag-grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

/**
 * Report-StudentList Component
 * Migrated from Report-StudentList.aspx
 *
 * Displays district-level student list report grouped by School → Year → Class
 * Features:
 * - Role-based District filtering (OWN_SCHOOL, OWN_DISTRICT, ALL)
 * - Calls stored procedure GetStudentList_sp with @ACTIVITY_ID and @DAERAH
 * - Groups students by School → Tahun → Kelas
 * - Shows student uniform sizes across multiple schools
 * - Excel/PDF export functionality
 */
const ReportStudentList = () => {
  const { t } = useTranslation(['reports', 'common']);

  // ========== STATE MANAGEMENT ==========

  // Auth/User State
  const [user, setUser] = useState(null);
  const [resourcePermissions, setResourcePermissions] = useState(null);

  // Filter State
  const [districts, setDistricts] = useState([]);
  const [activities, setActivities] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedActivity, setSelectedActivity] = useState('');

  // Data State
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ag-grid State
  const [gridApi, setGridApi] = useState(null);

  // Report Issue modal states
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  // ========== AG-GRID CONFIGURATION ==========

  // Column definitions for ag-grid with grouping
  const columnDefs = useMemo(() => [
    {
      headerName: t('reports:studentList.columns.no'),
      valueGetter: 'node.rowIndex + 1',
      width: 70,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentList.columns.schoolCode'),
      field: 'kod',
      width: 120,
      // rowGroup: true, // ❌ Removed - requires AG Grid Enterprise
      hide: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    {
      headerName: t('reports:studentList.columns.school'),
      field: 'sekolah',
      width: 250,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    {
      headerName: t('reports:studentList.columns.tahun'),
      field: 'tahun',
      width: 100,
      // rowGroup: true, // ❌ Removed - requires AG Grid Enterprise
      hide: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentList.columns.class'),
      field: 'class',
      width: 120,
      // rowGroup: true, // ❌ Removed - requires AG Grid Enterprise
      hide: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentList.columns.name'),
      field: 'nama',
      width: 300,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    {
      headerName: t('reports:studentList.columns.icNo'),
      field: 'noKP',
      width: 140,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => {
        if (params.value) {
          // Mask IC: show only last 4 digits (e.g., ********1234)
          return '********' + params.value.slice(-4);
        }
        return '';
      }
    },
    {
      headerName: t('reports:studentList.columns.gender'),
      field: 'jantina',
      width: 100,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentList.columns.bajuPutih'),
      field: 'bajuPutih',
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentList.columns.seluarPanjang'),
      field: 'seluarPanjang',
      width: 140,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentList.columns.seluarPendek'),
      field: 'seluarPendek',
      width: 140,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentList.columns.bajuKurung'),
      field: 'bajuKurung',
      width: 140,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentList.columns.kainKurung'),
      field: 'kainKurung',
      width: 140,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentList.columns.pinafore'),
      field: 'pinafore',
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentList.columns.kasut'),
      field: 'kasut',
      width: 100,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    }
  ], [t]);

  // Default column configuration
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: false
  }), []);

  // Grid options with grouping
  const gridOptions = useMemo(() => ({
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [30, 50, 100, 200],
    domLayout: 'autoHeight',
    suppressRowClickSelection: true,
    rowSelection: 'multiple',
    groupDisplayType: 'multipleColumns',
    groupDefaultExpanded: 0,
    autoGroupColumnDef: {
      headerName: t('reports:studentList.columns.group'),
      minWidth: 300,
      cellRendererParams: {
        suppressCount: false
      }
    }
  }), [t]);

  // Grid ready callback
  const onGridReady = (params) => {
    setGridApi(params.api);
    console.log('[ReportStudentList] ag-grid ready with grouping');
  };

  // ========== EFFECTS ==========

  // Load user and permissions on mount
  useEffect(() => {
    loadUser();
    loadResourcePermissions();
  }, []);

  // Load districts when user is loaded
  useEffect(() => {
    if (user) {
      loadDistricts();
    }
  }, [user]);

  // Load activities when district changes
  useEffect(() => {
    if (selectedDistrict) {
      loadActivities(selectedDistrict);
      setSelectedActivity('');
      setReportData(null);
    }
  }, [selectedDistrict]);

  // Auto-load data when activity changes
  useEffect(() => {
    if (selectedActivity && selectedDistrict) {
      console.log('[ReportStudentList] Activity selected, auto-loading data');
      loadReportData(selectedDistrict, selectedActivity);
    }
  }, [selectedActivity]);

  // ========== DATA LOADING FUNCTIONS ==========

  const loadUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        setUser(userData);
        console.log('[ReportStudentList] User loaded:', userData.email);
      } else {
        console.error('[ReportStudentList] No user found in localStorage');
        setError('User not found. Please login again.');
      }
    } catch (error) {
      console.error('[ReportStudentList] Error loading user:', error);
      setError('Failed to load user information');
    }
  };

  const loadResourcePermissions = () => {
    try {
      const permsStr = localStorage.getItem('resourcePermissions');
      if (permsStr) {
        const perms = JSON.parse(permsStr);
        setResourcePermissions(perms);
        console.log('[ReportStudentList] Resource permissions loaded');
      }
    } catch (error) {
      console.error('[ReportStudentList] Error loading permissions:', error);
    }
  };

  const loadDistricts = async () => {
    try {
      console.log('[ReportStudentList] Loading districts...');
      const response = await reportStudentListAPI.getDistricts();

      if (response.success && response.data) {
        setDistricts(response.data);
        console.log('[ReportStudentList] Loaded', response.data.length, 'districts');

        // Auto-select if only one district
        if (response.data.length === 1) {
          setSelectedDistrict(response.data[0].ppd);
        }
      } else {
        console.error('[ReportStudentList] Failed to load districts:', response);
        setError('Failed to load districts');
      }
    } catch (error) {
      console.error('[ReportStudentList] Error loading districts:', error);
      setError('Error loading districts: ' + error.message);
    }
  };

  const loadActivities = async (districtCode) => {
    try {
      console.log('[ReportStudentList] Loading activities for district:', districtCode);
      const response = await reportStudentListAPI.getActivities(districtCode);

      if (response.success && response.data) {
        setActivities(response.data);
        console.log('[ReportStudentList] Loaded', response.data.length, 'activities');

        // Auto-select first active activity
        const activeActivity = response.data.find(a => a.status === 'ACTIVE');
        if (activeActivity) {
          setSelectedActivity(activeActivity.activityId.toString());
        } else if (response.data.length > 0) {
          setSelectedActivity(response.data[0].activityId.toString());
        }
      } else {
        console.error('[ReportStudentList] Failed to load activities:', response);
        setError('Failed to load activities');
      }
    } catch (error) {
      console.error('[ReportStudentList] Error loading activities:', error);
      setError('Error loading activities: ' + error.message);
    }
  };

  const loadReportData = async (districtCode, activityId) => {
    try {
      setLoading(true);
      setError('');
      console.log('[ReportStudentList] Loading report data for district:', districtCode, 'activity:', activityId);

      const response = await reportStudentListAPI.getReportData(districtCode, parseInt(activityId));

      if (response.success && response.data) {
        setReportData(response.data);
        console.log('[ReportStudentList] Report data loaded:', response.data.totalStudents, 'students from', response.data.totalSchools, 'schools');
      } else {
        console.error('[ReportStudentList] Failed to load report data:', response);
        setError(response.message || 'Failed to load report data');
        setReportData(null);
      }
    } catch (error) {
      console.error('[ReportStudentList] Error loading report data:', error);
      setError('Error loading report data: ' + error.message);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  // ========== EXPORT FUNCTIONS (SERVER-SIDE with ClosedXML) ==========

  const handleExportExcel = async () => {
    if (!reportData || !selectedDistrict || !selectedActivity) {
      Swal.fire({
        icon: 'warning',
        title: t('reports:studentList.messages.noData'),
        text: t('reports:studentList.messages.noDataToExport'),
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    try {
      console.log('[ReportStudentList] Server-side Excel export - District:', selectedDistrict, 'Activity:', selectedActivity);

      Swal.fire({
        title: t('reports:common.messages.generatingExcel'),
        text: t('reports:common.messages.pleaseWaitExcel'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Call server-side export API
      const response = await reportStudentListAPI.exportToExcel({
        districtCode: selectedDistrict,
        activityId: parseInt(selectedActivity),
        splitBy: 1, // 0=None, 1=School, 2=Tahun, 3=SchoolAndTahun (DEFAULT: Split by School)
        includeSubtotals: true,
        includeGrandTotal: true,
        showPageHeaders: true,
        applyFormatting: true,
        groupRows: false
      });

      if (response.success) {
        Swal.close();
        console.log('[ReportStudentList] Server-side Excel export completed');

        Swal.fire({
          icon: 'success',
          title: t('reports:common.messages.exportSuccess'),
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        throw new Error(response.message || 'Export failed');
      }
    } catch (error) {
      console.error('[ReportStudentList] Server-side Excel export failed:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.exportFailed'),
        text: error.message || t('reports:common.messages.exportFailedMessage'),
        confirmButtonColor: '#dc3545'
      });
    }
  };

  const handleExportPDF = async () => {
    if (!selectedDistrict || !selectedActivity) {
      Swal.fire({
        icon: 'warning',
        title: t('reports:studentList.messages.selectionRequired'),
        text: t('reports:studentList.messages.selectDistrictAndActivity'),
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    if (!reportData || !reportData.students || reportData.students.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: t('reports:studentList.messages.noData'),
        text: t('reports:studentList.messages.noDataToExport'),
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    try {
      console.log('[ReportStudentList] Exporting to PDF...');

      Swal.fire({
        title: t('reports:common.messages.generatingPDF'),
        text: t('reports:common.messages.pleaseWaitPDF'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Server-side PDF export
      const exportRequest = {
        districtCode: selectedDistrict,
        activityId: parseInt(selectedActivity),
        splitBy: 1, // Use enum value: 0=None, 1=School, 2=Tahun, 3=SchoolAndTahun
        includeSubtotals: true,
        showPageHeaders: true
      };

      console.log('[ReportStudentList] PDF Export Request:', JSON.stringify(exportRequest));

      await reportStudentListAPI.exportToPdf(exportRequest);

      Swal.fire({
        icon: 'success',
        title: t('reports:common.messages.exportSuccess'),
        text: t('reports:common.messages.pdfDownloaded'),
        confirmButtonColor: '#28a745'
      });

      console.log('[ReportStudentList] PDF export completed successfully');
    } catch (error) {
      console.error('[ReportStudentList] Server-side PDF export failed:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.exportFailed'),
        text: error.message || t('reports:common.messages.exportFailedMessage'),
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // OLD CLIENT-SIDE PDF CODE (NOT USED ANYMORE)
  const handleExportPDF_OLD_CLIENTSIDE = async () => {
    try {
      const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape orientation

      // Mask IC for privacy - show only last 4 digits
      const maskIC = (ic) => {
        if (!ic) return '';
        return '********' + ic.slice(-4);
      };

      // Group by school
      const groupedBySchool = {};
      reportData.students.forEach(student => {
        if (!groupedBySchool[student.kod]) {
          groupedBySchool[student.kod] = {
            name: student.sekolah,
            students: []
          };
        }
        groupedBySchool[student.kod].students.push(student);
      });

      let isFirstSchool = true;

      // Process each school
      Object.keys(groupedBySchool).forEach((schoolCode) => {
        const school = groupedBySchool[schoolCode];

        // Start each school on a new page (except the first one)
        if (!isFirstSchool) {
          pdf.addPage();
        }
        isFirstSchool = false;

        let yPosition = 15;

        // Add title
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text('SENARAI PELAJAR', pdf.internal.pageSize.getWidth() / 2, yPosition, { align: 'center' });
        yPosition += 10;

        // Add report info
        pdf.setFontSize(9);
        pdf.setFont(undefined, 'normal');
        pdf.text(`DAERAH: ${reportData.districtCode}`, 14, yPosition);
        yPosition += 5;
        pdf.text(`AKTIVITI: ${reportData.activityName}`, 14, yPosition);
        yPosition += 5;
        pdf.text(`TAHUN: ${reportData.year}`, 14, yPosition);
        yPosition += 5;
        pdf.text(`BIL. SEKOLAH: ${reportData.totalSchools}`, 14, yPosition);
        yPosition += 5;
        pdf.text(`BIL. PELAJAR: ${reportData.totalStudents}`, 14, yPosition);
        yPosition += 8;

        // Add school name
        pdf.setFontSize(11);
        pdf.setFont(undefined, 'bold');
        pdf.text(`SEKOLAH: ${schoolCode} - ${school.name}`, 14, yPosition);
        yPosition += 8;

        // Prepare table data
        const tableHeaders = [
          'No', 'School Code', 'School', 'Tahun', 'Kelas', 'Name', 'NoKP',
          'Jantina', 'Baju Putih', 'Seluar Panjang', 'Seluar Pendek',
          'Baju Kurung', 'Kain Kurung', 'Pinafore', 'Kasut'
        ];

        const tableData = school.students.map((student, index) => [
          index + 1,
          student.kod || '',
          student.sekolah || '',
          student.tahun || '',
          student.class || '',
          student.nama || '',
          maskIC(student.noKP),
          student.jantina || '',
          student.bajuPutih || '',
          student.seluarPanjang || '',
          student.seluarPendek || '',
          student.bajuKurung || '',
          student.kainKurung || '',
          student.pinafore || '',
          student.kasut || ''
        ]);

        // Add table using autoTable
        autoTable(pdf, {
          head: [tableHeaders],
          body: tableData,
          startY: yPosition,
          theme: 'grid',
          styles: {
            fontSize: 7,
            cellPadding: 1.5,
            halign: 'center',
            valign: 'middle'
          },
          headStyles: {
            fillColor: [0, 123, 255],
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center'
          },
          columnStyles: {
            0: { cellWidth: 8, halign: 'center' },   // No
            1: { cellWidth: 18, halign: 'center' },  // School Code
            2: { cellWidth: 35, halign: 'left' },    // School - wider and left-aligned
            3: { cellWidth: 12, halign: 'center' },  // Tahun
            4: { cellWidth: 15, halign: 'center' },  // Kelas
            5: { cellWidth: 40, halign: 'left' },    // Name - wider and left-aligned
            6: { cellWidth: 25, halign: 'center' },  // NoKP
            7: { cellWidth: 15, halign: 'center' },  // Jantina
            8: { cellWidth: 15, halign: 'center' },  // Baju Putih
            9: { cellWidth: 15, halign: 'center' },  // Seluar Panjang
            10: { cellWidth: 15, halign: 'center' }, // Seluar Pendek
            11: { cellWidth: 15, halign: 'center' }, // Baju Kurung
            12: { cellWidth: 15, halign: 'center' }, // Kain Kurung
            13: { cellWidth: 15, halign: 'center' }, // Pinafore
            14: { cellWidth: 12, halign: 'center' }  // Kasut
          },
          margin: { left: 10, right: 10 },
          didDrawPage: function (data) {
            // Add page footer
            pdf.setFontSize(8);
            pdf.setFont(undefined, 'normal');
            const pageStr = `Halaman ${pdf.internal.getCurrentPageInfo().pageNumber}`;
            pdf.text(pageStr, pdf.internal.pageSize.getWidth() - 30, pdf.internal.pageSize.getHeight() - 10);
          }
        });
      });

      // Save file
      const filename = `Student_List_${reportData.districtCode}_${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(filename);

      Swal.close();
      console.log('[ReportStudentList] PDF export completed with', Object.keys(groupedBySchool).length, 'schools');
    } catch (error) {
      console.error('[ReportStudentList] PDF export failed:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.exportFailed'),
        text: t('reports:common.messages.exportFailedMessage'),
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // Handle Report Issue button click
  const handleReportIssueClick = async () => {
    try {
      Swal.fire({
        title: t('reports:common.messages.capturingScreenshot'),
        html: t('reports:common.messages.pleaseWaitScreenshot'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        width: window.innerWidth,
        height: window.innerHeight
      });

      const imageData = canvas.toDataURL('image/png');
      setScreenshot(imageData);
      setShowReportIssueModal(true);
      Swal.close();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.captureFailed'),
        text: t('reports:common.messages.captureFailedMessage')
      });
    }
  };

  // ========== RENDER ==========

  return (
    <div className="report-student-list-container">
      {/* Header with Logo and Bilingual Title */}
      <div className="header-container">
        <img className="logo" src="/KTC.png" alt="KTC" />
        <h2>{t('reports:studentList.title')} ({t('reports:studentList.titleEn')})</h2>
        <button className="header-btn btn-report-issue" onClick={handleReportIssueClick} title={t('common:button.reportIssue', { lng: 'en', defaultValue: 'Report Issue' })}>
          <i className="fas fa-bug"></i>
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      <div className="filters-container">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="ddlDistrict">{t('reports:studentList.filters.districtPPD')}</label>
            <select
              id="ddlDistrict"
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
              disabled={loading}
              className="form-control"
            >
              <option value="">{t('reports:studentList.filters.selectDistrict')}</option>
              {districts.map(district => (
                <option key={district.ppd} value={district.ppd}>
                  {district.ppd}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="ddlActivity">{t('reports:studentList.filters.activityYear')}</label>
            <select
              id="ddlActivity"
              value={selectedActivity}
              onChange={(e) => setSelectedActivity(e.target.value)}
              disabled={loading || !selectedDistrict}
              className="form-control"
            >
              <option value="">{t('reports:studentList.filters.selectActivity')}</option>
              {activities.map(activity => (
                <option key={activity.activityId} value={activity.activityId}>
                  {activity.activityDisplay}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <button
              onClick={() => loadReportData(selectedDistrict, selectedActivity)}
              disabled={loading || !selectedDistrict || !selectedActivity}
              className="btn btn-primary"
            >
              {loading ? t('reports:studentList.buttons.loading') : t('reports:studentList.buttons.loadReport')}
            </button>
          </div>
        </div>
      </div>

      {reportData && (
        <>
          <div className="report-info">
            <div className="info-card">
              <span className="info-label">{t('reports:studentList.stats.district')}</span>
              <span className="info-value">{reportData.districtCode}</span>
            </div>
            <div className="info-card">
              <span className="info-label">{t('reports:studentList.stats.activity')}</span>
              <span className="info-value">{reportData.activityName}</span>
            </div>
            <div className="info-card">
              <span className="info-label">{t('reports:studentList.stats.year')}</span>
              <span className="info-value">{reportData.year}</span>
            </div>
            <div className="info-card">
              <span className="info-label">{t('reports:studentList.stats.totalSchools')}</span>
              <span className="info-value">{reportData.totalSchools}</span>
            </div>
            <div className="info-card">
              <span className="info-label">{t('reports:studentList.stats.totalStudents')}</span>
              <span className="info-value">{reportData.totalStudents}</span>
            </div>
          </div>

          <div className="export-buttons">
            <button onClick={handleExportExcel} className="btn btn-success">
              {t('reports:studentList.buttons.exportExcel')}
            </button>
            <button onClick={handleExportPDF} className="btn btn-danger">
              {t('reports:studentList.buttons.exportPDF')}
            </button>
          </div>

          <div className="ag-grid-container ag-theme-alpine">
            <AgGridReact
              columnDefs={columnDefs}
              rowData={reportData.students}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              onGridReady={onGridReady}
            />
          </div>
        </>
      )}

      {loading && (
        <div className="loading-spinner">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">{t('reports:studentList.buttons.loading')}</span>
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {showReportIssueModal && (
        <ReportIssueModal
          show={showReportIssueModal}
          onClose={() => setShowReportIssueModal(false)}
          screenshot={screenshot}
          context={{
            screen: 'Report Student List',
            selectedDistrict,
            selectedActivity,
            reportData
          }}
        />
      )}
    </div>
  );
};

export default ReportStudentList;
